import axios from 'axios';
import Cookies from 'js-cookie';

class TokenService {
    constructor() {
        this.refreshPromise = null;
        this.isRefreshing = false;
        this.lastRefreshTime = null; // track เวลา refresh ล่าสุด
        this.initializeTokenRefresh();
    }

    // Set token with proper expiration and sync storage
    setToken(token, uid = null) {
        if (!token) {
            console.error('❌ setToken called with no token');
            return false;
        }

        try {
            console.log('🔐 Setting token:', token.substring(0, 20) + '...');
            
            const expires = new Date();
            expires.setDate(expires.getDate() + 30); // 30 days from now

            // Set cookie with secure flags
            Cookies.set("jwt", token, { 
                expires: expires,
                secure: window.location.protocol === 'https:',
                sameSite: 'lax' 
            });

            // Sync with localStorage for backup
            localStorage.setItem("token", token);
            localStorage.setItem("token_expiry", expires.toISOString());
            
            if (uid) {
                localStorage.setItem("uidOfUser", uid);
                console.log('👤 UID set:', uid);
            }

            console.log('✅ Token set successfully');
            console.log('🍪 Cookie set:', Cookies.get("jwt") ? 'Success' : 'Failed');
            console.log('💾 localStorage set:', localStorage.getItem("token") ? 'Success' : 'Failed');
            
            return true;
        } catch (error) {
            console.error('❌ Error setting token:', error);
            return false;
        }
    }

    // ── "จำอุปกรณ์นี้ไว้" ──────────────────────────────────────────
    // เก็บเฉพาะ remember token ที่ server สุ่มมาให้ ไม่ใช่รหัสผ่าน
    // token นี้ใช้ได้ครั้งเดียว server จะออกอันใหม่ให้ทุกครั้งที่ใช้
    // จะถูกลบเฉพาะตอนกด "ออกจากระบบ" หรือ server บอกว่าใช้ไม่ได้แล้ว
    // ไม่ถูกลบตอน session หมดอายุ เพราะนั่นคือกรณีที่ต้องใช้มันพอดี
    setRememberToken(rememberToken) {
        if (!rememberToken) return;
        try {
            localStorage.setItem("remember_device", rememberToken);
        } catch (error) {
            console.error('Error saving remember token:', error);
        }
    }

    getRememberToken() {
        try {
            return localStorage.getItem("remember_device");
        } catch (error) {
            return null;
        }
    }

    clearRememberToken() {
        try {
            localStorage.removeItem("remember_device");
        } catch (error) {
            console.error('Error clearing remember token:', error);
        }
    }

    // เข้าสู่ระบบด้วย remember token ของอุปกรณ์ (ไม่ต้องใช้รหัสผ่าน)
    //   'ok'          = เข้าระบบสำเร็จ ตั้ง token ใหม่เรียบร้อย
    //   'invalid'     = server ปฏิเสธ ต้องกรอกรหัสผ่าน (ลบ remember token ทิ้ง)
    //   'none'        = เครื่องนี้ไม่มี remember token
    //   'unreachable' = ติดต่อ server ไม่ได้ ยังไม่ต้องลบอะไร
    async loginWithRememberToken() {
        const rememberToken = this.getRememberToken();
        if (!rememberToken) return 'none';

        try {
            // ใช้ instance เปล่า ไม่ผ่าน interceptor ที่แนบ Authorization / retry 401
            // เพราะ endpoint นี้ยืนยันตัวตนด้วย remember token ล้วน ๆ
            const response = await axios.create().post(
                `${process.env.REACT_APP_API}/auth/remember-login`,
                { remember_token: rememberToken },
                { timeout: 15000 }
            );

            if (!response.data.success || !response.data.token) {
                this.clearRememberToken();
                return 'invalid';
            }

            this.setToken(response.data.token, response.data.uid);
            // server หมุน token ใหม่ให้ทุกครั้ง ต้องเก็บอันใหม่ทับเสมอ
            this.setRememberToken(response.data.remember_token);
            return 'ok';

        } catch (error) {
            const status = error.response?.status;
            console.log('🔐 Remember login failed:', error.response?.data?.error || error.message);

            if (status === 400 || status === 401 || status === 403) {
                // 400 = โดน rate limit, 401/403 = token ใช้ไม่ได้แล้ว
                if (status !== 400) this.clearRememberToken();
                return 'invalid';
            }
            return 'unreachable';
        }
    }

    // Get token with fallback and validation
    getToken() {
        try {
            // Try cookie first (more secure)
            let token = Cookies.get("jwt");
            
            // Fallback to localStorage
            if (!token) {
                token = localStorage.getItem("token");
            }

            if (!token) return null;

            // Check localStorage expiry if exists
            const expiry = localStorage.getItem("token_expiry");
            if (expiry && new Date() > new Date(expiry)) {
                console.log('🔐 Token expired, clearing...');
                this.clearToken({ keepProfile: true });
                return null;
            }

            return token;
        } catch (error) {
            console.error('Error getting token:', error);
            return null;
        }
    }

    // ตรวจสอบ session กับ server แบบแยกสาเหตุได้ 3 แบบ
    //   'valid'       = server ยืนยันว่า token ใช้ได้
    //   'invalid'     = server ยืนยันว่า token ใช้ไม่ได้ (401/403) → logout ได้
    //   'unreachable' = เน็ตหลุด / timeout / server error → "ไม่รู้" ห้าม logout เด็ดขาด
    async checkSession() {
        const token = this.getToken();
        if (!token) return 'invalid';

        try {
            const response = await axios.post(`${process.env.REACT_APP_API}/auth/validate-token`, {}, {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 10000
            });

            return response.data.valid ? 'valid' : 'invalid';
        } catch (error) {
            const status = error.response?.status;
            console.log('🔐 Token validation failed:', error.response?.data?.error || error.message);

            if (status === 401 || status === 403) return 'invalid';
            return 'unreachable';
        }
    }

    // Validate token with server (boolean wrapper, เก็บไว้เพื่อ backward compatibility)
    async validateToken() {
        return (await this.checkSession()) === 'valid';
    }

    // Refresh token seamlessly (boolean wrapper)
    async refreshToken(force = false) {
        return (await this.refreshSession(force)) === 'refreshed';
    }

    // ต่ออายุ session โดยคืนสถานะแบบแยกสาเหตุ เหมือน checkSession()
    //   'refreshed'   = ได้ token ใหม่แล้ว
    //   'invalid'     = server ปฏิเสธ token (401/403) → logout ได้
    //   'unreachable' = เน็ตหลุด / timeout / server error → ห้าม logout
    async refreshSession(force = false) {
        // Prevent multiple concurrent refresh requests
        if (this.isRefreshing && !force) {
            return this.refreshPromise;
        }

        const currentToken = this.getToken();
        if (!currentToken) {
            console.log('🔐 No token to refresh');
            return 'invalid';
        }

        this.isRefreshing = true;

        this.refreshPromise = new Promise(async (resolve) => {
            try {
                console.log('🔄 Refreshing token...');

                const response = await axios.post(`${process.env.REACT_APP_API}/auth/refresh-token`, {}, {
                    headers: { Authorization: `Bearer ${currentToken}` },
                    timeout: 15000
                });

                if (response.data.success) {
                    const newToken = response.data.token;
                    const uid = response.data.uid;

                    this.setToken(newToken, uid);
                    this.lastRefreshTime = Date.now();
                    console.log('✅ Token refreshed successfully');

                    this.isRefreshing = false;
                    resolve('refreshed');
                } else {
                    throw new Error(response.data.error || 'Token refresh failed');
                }

            } catch (error) {
                console.error('🔐 Token refresh failed:', error.response?.data?.error || error.message);

                // ลบ token เฉพาะเมื่อ server บอกว่า token ไม่ valid (401/403)
                // ถ้าเป็น network error / timeout → เก็บ token เดิมไว้ก่อน ห้าม logout
                const status = error.response?.status;
                this.isRefreshing = false;

                if (status === 401 || status === 403) {
                    this.clearToken({ keepProfile: true });
                    resolve('invalid');
                    return;
                }
                resolve('unreachable');
            }
        });

        return this.refreshPromise;
    }

    // Clear all token data
    // keepProfile = true → เก็บชื่อผู้ใช้ + รูปโปรไฟล์ไว้ สำหรับการ์ด "เข้าสู่ระบบด่วน"
    //   ใช้ตอน session หมดอายุเอง (ไม่ใช่การกดออกจากระบบ)
    //   ข้อมูลที่เก็บไว้เป็นแค่ชื่อผู้ใช้กับ URL รูป ไม่มีรหัสผ่านหรือ token
    clearToken({ keepProfile = false } = {}) {
        try {
            Cookies.remove("jwt");
            localStorage.removeItem("token");
            localStorage.removeItem("token_expiry");
            localStorage.removeItem("uidOfUser");

            // legacy: เวอร์ชันเก่าเคยเก็บรหัสผ่านเป็น plaintext ไว้ใน localStorage
            // ลบทิ้งทุกครั้งที่มีโอกาส และไม่เขียนกลับอีก
            localStorage.removeItem("194aa3da_password");

            if (!keepProfile) {
                localStorage.removeItem("194aa3da_username");
                localStorage.removeItem("profile_user");
                localStorage.removeItem("remember_device");
            }

            console.log('🧹 Token cleared', keepProfile ? '(keep profile hint)' : '(full)');
        } catch (error) {
            console.error('Error clearing token:', error);
        }
    }

    // ออกจากระบบ — ทางเดียวของทั้งแอป ห้ามลบ token มือ ๆ ที่อื่นอีก
    //
    // ที่นี่แยก 2 ระดับ อย่าสับสนกัน:
    //   logout()       = จบ session แต่ "เครื่องนี้ยังถูกจำไว้"
    //                    ผู้ใช้แตะการ์ดเข้าสู่ระบบด่วนแล้วกลับเข้าได้เลย ไม่ต้องกรอกรหัสผ่าน
    //   forgetDevice() = เลิกจำเครื่องนี้จริง ๆ เพิกถอน remember token ที่ server ด้วย
    //                    ต้องใช้อันนี้กับเครื่องที่ใช้ร่วมกับคนอื่น
    //
    // ข้อแลกเปลี่ยนที่ต้องรู้: หลัง logout() ใครที่หยิบเครื่องนี้ไปต่อ
    // สามารถแตะการ์ดแล้วเข้าบัญชีนี้ได้ทันที เพราะ remember token ยังอยู่
    async logout() {
        // ตั้งธงกัน auto-login เด้งกลับเองตอนโหลดหน้า
        // ผู้ใช้ต้อง "แตะ" เองเท่านั้นถึงจะกลับเข้าระบบ ไม่งั้น logout จะดูเหมือนไม่ทำงาน
        this.markIntentionalLogout();

        // ล้างเฉพาะ session ปัจจุบัน เก็บชื่อ/รูป/remember token ไว้
        this.clearToken({ keepProfile: true });
    }

    // เลิกจำอุปกรณ์นี้ถาวร — เพิกถอนที่ server แล้วล้างร่องรอยในเครื่องให้หมด
    async forgetDevice() {
        const rememberToken = this.getRememberToken();

        // ล้างฝั่งเครื่องก่อน ต่อให้เรียก server ไม่สำเร็จก็ต้องเลิกจำให้ได้
        this.clearRememberToken();
        this.clearToken();

        if (rememberToken) {
            try {
                await axios.create().post(
                    `${process.env.REACT_APP_API}/auth/forget-device`,
                    { remember_token: rememberToken },
                    { timeout: 10000 }
                );
            } catch (error) {
                console.error('Forget device failed:', error.response?.data?.error || error.message);
            }
        }
    }

    // ธงกัน auto-login เด้งกลับเองหลังผู้ใช้กดออกจากระบบ
    // ใช้ localStorage ไม่ใช่ sessionStorage เพื่อให้เจตนา "ฉันออกจากระบบแล้ว"
    // อยู่ข้ามการปิด/เปิดแท็บด้วย พฤติกรรมจะได้คงเส้นคงวา:
    // ออกจากระบบแล้วต้อง "แตะ" การ์ดเองหนึ่งครั้งเสมอ ระบบจะไม่พาเข้าเอง
    markIntentionalLogout() {
        try {
            localStorage.setItem("intentional_logout", "1");
        } catch (error) {
            // localStorage อาจใช้ไม่ได้ในโหมดส่วนตัวบางเบราว์เซอร์
        }
    }

    // อ่านอย่างเดียว ไม่ล้างธง เพื่อไม่ให้ StrictMode ที่รัน effect ซ้ำ
    // ทำให้ครั้งที่สองเผลอ auto-login
    wasIntentionalLogout() {
        try {
            return localStorage.getItem("intentional_logout") === "1";
        } catch (error) {
            return false;
        }
    }

    // ล้างธงเมื่อผู้ใช้แสดงเจตนาจะเข้าระบบอีกครั้ง (กดปุ่ม/กรอกฟอร์มเอง)
    clearIntentionalLogout() {
        try {
            localStorage.removeItem("intentional_logout");
        } catch (error) {
            // ไม่เป็นไร
        }
    }

    // Check if user is logged in
    isLoggedIn() {
        return this.getToken() !== null;
    }

    // Get user ID
    getUserId() {
        return localStorage.getItem("uidOfUser");
    }

    // รอ refresh ที่กำลังทำงานอยู่ให้เสร็จ แล้วคืน token ปัจจุบัน
    // ใช้เรียกก่อน API call สำคัญ เช่น lead submission
    async ensureFreshToken() {
        if (this.isRefreshing) {
            console.log('⏳ Waiting for ongoing token refresh before proceeding...');
            await this.refreshPromise;
        }
        return this.getToken();
    }

    // Initialize automatic token refresh
    initializeTokenRefresh() {
        // Refresh token every 25 minutes (before 30-minute cache expires)
        setInterval(async () => {
            if (this.isLoggedIn()) {
                await this.refreshToken();
            }
        }, 25 * 60 * 1000); // 25 minutes

        // Handle page visibility change (mobile browser return)
        // จำกัดการ refresh ให้ห่างกันอย่างน้อย 5 นาที
        // กัน race condition กับ API call ที่กำลังทำงานอยู่
        const MIN_REFRESH_INTERVAL = 5 * 60 * 1000; // 5 นาที
        document.addEventListener('visibilitychange', async () => {
            if (!document.hidden && this.isLoggedIn()) {
                const now = Date.now();
                const timeSinceLastRefresh = this.lastRefreshTime
                    ? now - this.lastRefreshTime
                    : Infinity;

                if (timeSinceLastRefresh > MIN_REFRESH_INTERVAL) {
                    console.log('📱 Page visible - refreshing token...');
                    await this.refreshToken();
                } else {
                    console.log('📱 Page visible - token recently refreshed, skipping');
                }
            }
        });

        // Handle beforeunload to save state
        window.addEventListener('beforeunload', () => {
            // Sync token state before page unload
            const token = this.getToken();
            if (token) {
                localStorage.setItem('token_last_seen', new Date().toISOString());
            }
        });

        // Handle page load - check if we returned from background
        window.addEventListener('load', async () => {
            const lastSeen = localStorage.getItem('token_last_seen');
            const token = this.getToken();
            
            if (token && lastSeen) {
                const timeSinceLastSeen = new Date() - new Date(lastSeen);
                
                // If more than 5 minutes since last seen, refresh token
                if (timeSinceLastSeen > 5 * 60 * 1000) {
                    console.log('📱 Returned after', Math.round(timeSinceLastSeen / 60000), 'minutes - refreshing token...');
                    await this.refreshToken();
                }
            }
            
            localStorage.removeItem('token_last_seen');
        });
    }

    // Enhanced axios interceptor for automatic token handling
    setupAxiosInterceptor(axiosInstance = axios) {
        // Request interceptor - add token to requests
        axiosInstance.interceptors.request.use(
            (config) => {
                const token = this.getToken();
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        // Response interceptor - handle token expiry
        axiosInstance.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;

                if (error.response?.status === 401 && !originalRequest._retry) {
                    originalRequest._retry = true;

                    // Try to refresh token
                    const refreshed = await this.refreshToken();
                    
                    if (refreshed) {
                        // Retry original request with new token
                        const newToken = this.getToken();
                        originalRequest.headers.Authorization = `Bearer ${newToken}`;
                        return axiosInstance(originalRequest);
                    } else {
                        // Refresh failed - allow guest access to continue
                        console.log('🔐 Token refresh failed - allowing guest access');
                        return Promise.reject(error);
                    }
                }

                return Promise.reject(error);
            }
        );
    }
}

// Create singleton instance
const tokenService = new TokenService();

// Setup axios interceptor globally
tokenService.setupAxiosInterceptor();

export default tokenService;